import { useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useSubscriptionStore } from '@/features/game/store/subscriptionStore';
import { Logo } from '@/components/shared/Logo';
import { Plus, User, LogOut, Gamepad2, ShoppingCart, Loader2, Sparkles } from 'lucide-react';

const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

export const PlayerLayout = () => {
  const { user, logout, isLoggingOut } = useAuthStore();
  const { status: subscriptionStatus, fetchStatus } = useSubscriptionStore();
  const navigate = useNavigate();

  // Fetch subscription status on mount
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Get games display text
  const getGamesDisplayText = () => {
    if (!subscriptionStatus) return 'جاري التحميل...';
    if (subscriptionStatus.canPlayFree) return 'لديك تجربة مجانية!';
    if (subscriptionStatus.gamesRemaining > 0) {
      return `عدد الألعاب المتبقية: ${subscriptionStatus.gamesRemaining}`;
    }
    return 'لا توجد ألعاب متبقية';
  };

  const getGamesBadgeStyle = () => {
    if (!subscriptionStatus) return 'bg-gray-400';
    if (subscriptionStatus.canPlayFree) return 'bg-green-500';
    if (subscriptionStatus.gamesRemaining > 0) return 'bg-green-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-gray-50" style={arabicFont}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Right - Logo & Nav */}
          <div className="flex items-center gap-8">
            <Link to="/player">
              <Logo size="xl" animateImage />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/player" className="text-green-600 font-bold text-lg hover:text-green-700 transition-colors">
                العب
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-green-600 font-bold text-lg transition-colors">
                تواصل معنا
              </Link>
              <Link to="/player/my-games" className="text-gray-600 hover:text-green-600 font-bold text-lg transition-colors">
                ألعابي
              </Link>
            </nav>
          </div>

          {/* Left - User Info */}
          <div className="flex items-center gap-3">
            {/* Games Count Badge */}
            <div className={`hidden sm:flex items-center gap-2 ${getGamesBadgeStyle()} text-white px-4 py-2 rounded-full`}>
              {subscriptionStatus?.canPlayFree ? (
                <Sparkles className="w-4 h-4" />
              ) : subscriptionStatus?.gamesRemaining && subscriptionStatus.gamesRemaining > 0 ? (
                <Plus className="w-4 h-4" />
              ) : (
                <ShoppingCart className="w-4 h-4" />
              )}
              <span className="font-bold text-sm">{getGamesDisplayText()}</span>
            </div>

            {/* User Profile Dropdown */}
            <div className="relative group">
              <div className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <span className="hidden sm:block font-bold text-gray-700">
                  {user?.displayName || user?.username || 'اللاعب'}
                </span>
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
              </div>
              
              {/* Dropdown Menu */}
              <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <Link 
                  to="/player/profile" 
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-t-xl transition-colors"
                >
                  <User className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">حسابي</span>
                </Link>
                <Link 
                  to="/player/my-games" 
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <Gamepad2 className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">ألعابي</span>
                </Link>
                <button 
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 rounded-b-xl transition-colors w-full text-right disabled:opacity-50"
                >
                  {isLoggingOut ? (
                    <Loader2 className="w-5 h-5 text-red-500 animate-spin" />
                  ) : (
                    <LogOut className="w-5 h-5 text-red-500" />
                  )}
                  <span className="font-medium text-red-500">
                    {isLoggingOut ? 'جاري الخروج...' : 'تسجيل الخروج'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
};
